// @ts-ignore
import domtoimage from 'dom-to-image'

// @ts-ignore
import { saveAs } from 'file-saver'

import { useCallback, useMemo } from 'react'
import XLSX from 'xlsx'
import {
    NyuDataPointsWithParsedTime,
    NyuDataProperties,
    NyuSeriesProperties,
} from '../../../shared/types'
import useGetLineData from '../hooks/useGetLineData'
import useGlobalState from '../hooks/useGlobalState'
import {
    forecastPostfix,
    highlightPostfix,
    isForecastDataKey,
    isHighlightDataKey,
    resolveSeriesKey,
    unwrapValueInArray,
} from '../utils/helpers'
import Dropdown from './Dropdown'
import { cn } from '../utils/cn'

type TableRows = (string | number)[][]

type Action = {
    label: string
    execute: () => void
}

// keeps filenames below OS limits (255 bytes per name, 260 chars per path on Windows);
// the full title and selection are still contained in the file itself
const MAX_FILENAME_LENGTH = 150

function titleToFilename(title: string): string {
    const sanitized = title.replace(/:/g, ' -').replace(/\s+/g, ' ').trim()
    return sanitized.length > MAX_FILENAME_LENGTH
        ? sanitized.slice(0, MAX_FILENAME_LENGTH).trim()
        : sanitized
}

function csvEncode(tableRows: TableRows): string {
    return tableRows
        .map((row) =>
            row
                .map((cell) => {
                    const escapedCell = String(cell).replace(/"/g, '""')
                    return '"' + escapedCell + '"'
                })
                .join(',')
        )
        .join('\n')
}

const stringToByteArray = (s: string): ArrayBuffer => {
    const buf = new ArrayBuffer(s.length)
    const view = new Uint8Array(buf)
    for (let i = 0; i < s.length; i += 1) {
        view[i] = s.charCodeAt(i) & 0xff
    }
    return buf
}

const saveImageSnapshot = async (
    title: string,
    targetNode: HTMLElement | null
): Promise<void> => {
    if (!targetNode) {
        return
    }

    try {
        const dataUrl = await domtoimage.toPng(targetNode)
        saveAs(dataUrl, `${titleToFilename(title)}.png`)
    } catch (error) {
        console.error('Error while creating your snapshot: ', error)
    }
}

const saveTableAsCSV = (title: string, tableRows: TableRows): void => {
    const csvContent = csvEncode(tableRows)
    saveAs(
        new Blob([csvContent], {
            type: 'text/csv;charset=utf-8',
        }),
        `${titleToFilename(title)}.csv`,
        { autoBom: true }
    )
}

const saveTableAsExcel = (title: string, tableRows: TableRows): void => {
    const ws = XLSX.utils.aoa_to_sheet(tableRows)
    const wb = XLSX.utils.book_new()

    const sheetName = 'GCT Sheet'
    wb.Sheets[sheetName] = ws
    wb.SheetNames.push(sheetName)

    wb.Props = {
        Title: title,
        CreatedDate: new Date(),
    }

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' })

    saveAs(
        new Blob([stringToByteArray(wbout)], {
            type: 'application/octet-stream',
        }),
        `${titleToFilename(title)}.xlsx`
    )
}

// A temporary component until we have a proper dropdown component
function ActionDropdown({ actions }: { actions: Action[] }) {
    const setState = useGlobalState((state) => state.setState)
    const setDownloadView = (downloadView: boolean) =>
        setState({ downloadView })

    const actionLabels = useMemo(() => {
        return actions.map((item) => item.label)
    }, [actions])

    const handleAction = async (label: string) => {
        const action = actions.find((a) => a.label === label)
        if (!action) {
            return
        }

        try {
            await setDownloadView(true)

            // wait for the download-view to render completely, otherwise the chart will be cut off on the right
            await new Promise((resolve) => setTimeout(resolve, 0))

            await action.execute()
        } finally {
            await setDownloadView(false)
        }
    }

    return (
        <Dropdown
            value="Download"
            testId={'download-button'}
            options={actionLabels}
            setValue={handleAction}
            className="w-24 text-center"
        />
    )
}

const defaultCite = `Steven A. Altman and Caroline R. Bastian (${new Date().getFullYear()}). DHL Globalization Tracker. DHL Group.`

export default function DownloadDataButton({
    title,
    dataPoints,
    dataProperties,
    seriesProperties,
    activeDataPointKeys,
    prependRows = [],
    cite = defaultCite,
    pullRight = false,
}: {
    title: string
    dataPoints: NyuDataPointsWithParsedTime
    dataProperties: NyuDataProperties
    seriesProperties: NyuSeriesProperties
    activeDataPointKeys: string[]
    prependRows?: string[][]
    cite?: string
    pullRight?: boolean
}) {
    const templateNode = useGlobalState((state) => state.templateNode)

    const getLineData = useGetLineData(seriesProperties)

    const getTableRows = useCallback(() => {
        // keep the order of the active keys
        const allKeys = new Set(
            dataPoints.flatMap((dataPoint) => Object.keys(dataPoint))
        )

        // a highlight sharing its base series' group is only a style of the base
        // line, so its values go into the base column instead of one of its own
        const mergedHighlightKeys = new Set(
            seriesProperties
                .map(({ key }) => key)
                .filter(
                    (key) =>
                        isHighlightDataKey(key) &&
                        resolveSeriesKey(key, seriesProperties) !== key
                )
        )

        const activeDataPointKeysWithForecast = seriesProperties.flatMap(
            ({ key }) => {
                if (
                    !activeDataPointKeys.includes(key) ||
                    mergedHighlightKeys.has(key)
                ) {
                    return []
                }
                const forecastKey = `${key}${forecastPostfix}`
                const addForecastKey =
                    allKeys.has(forecastKey) &&
                    !activeDataPointKeys.includes(forecastKey)
                return addForecastKey ? [key, forecastKey] : [key]
            }
        )

        const isBarChart = dataProperties.chartType === 'bar'

        const dataRows = dataPoints.map((dataPoint) => [
            isBarChart ? dataPoint.name : dataPoint.tooltipTimeValue,
            ...activeDataPointKeysWithForecast.map((key) => {
                if (
                    isForecastDataKey(key) &&
                    dataPoint[key.replace(forecastPostfix, '')] !== undefined
                ) {
                    // when there is both an actual value and a forecast value, we should not include the forecast value
                    return ''
                }

                const highlightKey = `${key}${highlightPostfix}`
                const highlightValue = mergedHighlightKeys.has(highlightKey)
                    ? dataPoint[highlightKey]
                    : undefined

                return dataPoint[key!] ?? highlightValue ?? ''
            }),
        ])

        const labelOrPeriod =
            dataProperties.chartType === 'bar' ? 'Label' : 'Period'

        const headers = activeDataPointKeysWithForecast.map((key) => {
            const lineData = getLineData(key)
            // a forecast series listed under its base series' label says so in
            // the column head; one with a label of its own speaks for itself
            if (isForecastDataKey(key) && lineData.key !== key) {
                return `${lineData.label} (forecast)`
            }
            return lineData.label
        })

        const hasMultipleSources =
            Array.isArray(dataProperties.source) &&
            dataProperties.source.length > 1

        const note = dataProperties.note
            ? `Note: ${unwrapValueInArray(dataProperties.note)}`
            : ''
        const source = `Data source${hasMultipleSources ? 's' : ''}: ${unwrapValueInArray(dataProperties.source)}`

        return [
            ...prependRows,
            [labelOrPeriod, ...headers], // header
            ...dataRows,
            [], // padding
            [cite], // footer
            [source],
            [note],
        ].map((row) => row.map((cell) => (cell === undefined ? '' : cell)))
    }, [
        dataPoints,
        activeDataPointKeys,
        getLineData,
        dataProperties,
        seriesProperties,
        prependRows,
    ])

    const actions: Action[] = useMemo(() => {
        return [
            {
                label: 'Image',
                execute: () => saveImageSnapshot(title, templateNode),
            },
            {
                label: 'CSV',
                execute: () => saveTableAsCSV(title, getTableRows()),
            },
            {
                label: 'XLSX',
                execute: () => saveTableAsExcel(title, getTableRows()),
            },
        ]
    }, [getTableRows, title, templateNode])

    return (
        <div className={cn('flex', pullRight && 'ml-auto')}>
            <ActionDropdown actions={actions} />
        </div>
    )
}
